import { useEffect, useState } from 'react';
import { SimpleSelect } from '@tpct/admin-ui';
import { __ } from '@/hub-shared/utils/i18n';
import { errorToUserFacingMessage } from '../../api/apiErrors';
import type { MissionApiConfig } from '../../types/missionPortal';
import { fetchMissionResponsables, updateMissionSetup } from '@tpct/mission-core/utils/missionStartApi';
import { missionRoleLabel } from '../../utils/missionRoleLabels';
import MissionUserLink from './MissionUserLink';

type MissionResponsableCardProps = {
  apiConfig: MissionApiConfig;
  missionId: number;
  displayName: string | null | undefined;
  responsableUserId: number | null | undefined;
  readOnly: boolean;
  currentUserId: number;
  portalPath: string;
  onNavigate: (path: string) => void;
  onUpdated: () => Promise<void>;
  onError: (message: string) => void;
};

const MissionResponsableCard = ({
  apiConfig,
  missionId,
  displayName,
  responsableUserId,
  readOnly,
  currentUserId,
  portalPath,
  onNavigate,
  onUpdated,
  onError,
}: MissionResponsableCardProps) => {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [options, setOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | ''>(
    responsableUserId !== null && responsableUserId !== undefined && responsableUserId > 0
      ? responsableUserId
      : '',
  );

  useEffect(() => {
    if (!editing) {
      return;
    }

    let active = true;
    const run = async () => {
      try {
        const data = await fetchMissionResponsables(apiConfig, missionId);
        if (!active) {
          return;
        }
        setOptions(data.items.map((item) => ({
          value: String(item.id),
          label: `${item.display_name} (${missionRoleLabel(item.role)})`,
        })));
      } catch (e) {
        if (active) {
          onError(errorToUserFacingMessage(e, __('Impossible de charger les responsables de mission.')));
          setEditing(false);
        }
      }
    };

    void run();
    return () => {
      active = false;
    };
  }, [apiConfig, editing, missionId, onError]);

  useEffect(() => {
    if (responsableUserId !== null && responsableUserId !== undefined && responsableUserId > 0) {
      setSelectedUserId(responsableUserId);
    }
  }, [responsableUserId]);

  const saveResponsable = async () => {
    if (selectedUserId === '' || busy) {
      return;
    }

    setBusy(true);
    onError('');
    try {
      await updateMissionSetup(apiConfig, missionId, { responsable_user_id: selectedUserId });
      setEditing(false);
      await onUpdated();
    } catch (e) {
      onError(errorToUserFacingMessage(e, __('Impossible de mettre à jour le responsable de mission.')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="container-style mission-page__card" aria-labelledby="mission-responsable-heading">
      <h2 id="mission-responsable-heading" className="h4">{__('Responsable de mission')}</h2>
      {editing ? (
        <>
          <label className="visually-hidden" htmlFor="mission-responsable-select">{__('Responsable de mission')}</label>
          <SimpleSelect
            id="mission-responsable-select"
            options={options}
            value={selectedUserId === '' ? '' : String(selectedUserId)}
            onChange={(value) => setSelectedUserId(value === '' ? '' : Number(value))}
            placeholder={__('Sélectionner un responsable')}
            disabled={busy}
          />
          <div className="mission-page__card-actions">
            <button type="button" className="btn-primary" disabled={busy || selectedUserId === ''} onClick={() => { void saveResponsable(); }}>
              {busy ? __('Enregistrement...') : __('Enregistrer')}
            </button>
            <button type="button" className="btn-secondary" disabled={busy} onClick={() => setEditing(false)}>
              {__('Annuler')}
            </button>
          </div>
        </>
      ) : (
        <>
          {responsableUserId !== null && responsableUserId !== undefined && responsableUserId > 0 && displayName ? (
            <p>
              <MissionUserLink
                userId={responsableUserId}
                displayName={displayName}
                currentUserId={currentUserId}
                portalPath={portalPath}
                onNavigate={onNavigate}
              />
            </p>
          ) : (
            <p>{displayName ?? __('Non défini')}</p>
          )}
          {!readOnly ? (
            <button type="button" className="btn-primary mission-responsable__change-btn" onClick={() => setEditing(true)}>
              {__('Changer de responsable')}
            </button>
          ) : null}
        </>
      )}
    </section>
  );
};

export default MissionResponsableCard;
